import { config } from './config';
import { getAuthHeaders } from './session';

/**
 * LE TEMPS RÉEL, CÔTÉ ÉCRAN — ce qu'un collègue change, on le voit sans rien
 * faire.
 *
 * ---------------------------------------------------------------------------
 * POURQUOI `fetch` ET NON `EventSource`
 *
 * `EventSource` ne sait pas poser d'en-tête. En production l'authentification
 * passe par un cookie, il aurait donc marché ; en développement elle passe par
 * `X-Debug-User`, et le flux aurait été le SEUL appel de l'application à ne pas
 * fonctionner en local — la meilleure façon de livrer une panne qu'on ne
 * découvre qu'en recette. `fetch` accepte les en-têtes ET les cookies, au prix
 * d'une vingtaine de lignes pour découper le flux nous-mêmes.
 *
 * CE QUE LE FLUX APPORTE : un domaine qui a bougé, et qui l'a fait bouger. Rien
 * d'autre — pas de donnée, pas de nom. L'écran prévenu REDEMANDE par les routes
 * normales, celles qui savent qui a le droit de voir quoi. C'est ce qui rend le
 * temps réel sûr sans y recopier les règles de visibilité.
 * ---------------------------------------------------------------------------
 */

interface LiveEvent {
  domaine: string;
  acteur: string | null;
  sandbox: string | null;
}

type Abonnement = { domaines: string[]; rafraichir: () => void | Promise<void> };

const abonnements = new Set<Abonnement>();

let flux: AbortController | null = null;
let moi: string | null = null;
/** Report de reconnexion, qui s'allonge à chaque échec (plafonné). */
let attente = 1_000;
let minuterie: ReturnType<typeof setTimeout> | null = null;

/**
 * Les domaines touchés depuis le dernier rafraîchissement.
 *
 * ON GROUPE PLUTÔT QUE D'ENCHAÎNER. Déplacer cinq projets en lot produit cinq
 * événements en une seconde ; rafraîchir cinq fois ferait cinq fois le même
 * travail et ferait clignoter l'écran quatre fois pour rien.
 */
const enAttente = new Set<string>();
let groupement: ReturnType<typeof setTimeout> | null = null;
const DELAI_GROUPEMENT_MS = 350;

function declencher(): void {
  const domaines = [...enAttente];
  enAttente.clear();
  for (const a of abonnements) {
    if (a.domaines.some((d) => domaines.includes(d))) void a.rafraichir();
  }
}

function recevoir(evt: LiveEvent): void {
  /*
   * ON S'IGNORE SOI-MÊME. Celui qui vient d'agir a déjà son écran à jour, en
   * place (convention #72) : le recharger ne montrerait rien de neuf et
   * annulerait la saisie en cours dans le pire des cas.
   */
  if (evt.acteur && evt.acteur === moi) return;
  enAttente.add(evt.domaine);
  if (groupement) clearTimeout(groupement);
  groupement = setTimeout(declencher, DELAI_GROUPEMENT_MS);
}

async function ecouter(signal: AbortSignal): Promise<void> {
  const res = await fetch(`${config.API_BASE_URL}/events`, {
    headers: { ...getAuthHeaders(), Accept: 'text/event-stream' },
    signal,
  });
  if (!res.ok || !res.body) throw new Error(`Flux refusé (${res.status})`);

  // Connexion établie : on repart d'une attente courte pour la prochaine coupure.
  attente = 1_000;

  const lecteur = res.body.getReader();
  const decodeur = new TextDecoder();
  let tampon = '';

  for (;;) {
    const { done, value } = await lecteur.read();
    if (done) break;
    tampon += decodeur.decode(value, { stream: true });

    // Un message SSE se termine par une ligne vide ; le reste attend la suite.
    let coupure = tampon.indexOf('\n\n');
    while (coupure !== -1) {
      const bloc = tampon.slice(0, coupure);
      tampon = tampon.slice(coupure + 2);
      for (const ligne of bloc.split('\n')) {
        // Les lignes de commentaire (`: battement`) ne sont pas des messages.
        if (!ligne.startsWith('data:')) continue;
        try {
          recevoir(JSON.parse(ligne.slice(5).trim()) as LiveEvent);
        } catch {
          /* Une ligne illisible ne doit pas rompre le flux. */
        }
      }
      coupure = tampon.indexOf('\n\n');
    }
  }
}

/**
 * Ouvre (ou rouvre) le flux. Idempotent : rappeler pendant qu'il tourne ne
 * fait rien. À rappeler quand le contexte change — connexion, sortie du mode
 * découverte —, car les en-têtes envoyés changent avec lui.
 */
export function demarrerTempsReel(userId: string | null): void {
  arreterTempsReel();
  moi = userId;
  if (!userId) return;

  const controleur = new AbortController();
  flux = controleur;

  void ecouter(controleur.signal)
    .catch(() => undefined)
    .finally(() => {
      if (controleur.signal.aborted) return;
      /*
       * RECONNEXION À DÉLAI CROISSANT. Une API qui redémarre coupe tous les
       * flux d'un coup : sans ce report, chaque onglet ouvert la rappellerait
       * aussitôt, tous ensemble, au moment précis où elle a le moins de
       * ressources. Plafonné à 30 s pour que la reprise reste rapide.
       */
      minuterie = setTimeout(() => demarrerTempsReel(moi), attente);
      attente = Math.min(attente * 2, 30_000);
    });
}

/**
 * Rouvre le flux avec le contexte courant, sans changer d'utilisateur. À appeler
 * quand les en-têtes envoyés changent — l'entrée ou la sortie du mode
 * découverte, qui bascule le pont `X-Sandbox`.
 */
export function relancerTempsReel(): void {
  if (moi) demarrerTempsReel(moi);
}

export function arreterTempsReel(): void {
  if (minuterie) clearTimeout(minuterie);
  minuterie = null;
  flux?.abort();
  flux = null;
}

/**
 * Abonne un écran (ou un magasin) à des domaines. Renvoie de quoi se désabonner.
 *
 * Le rafraîchissement reste à la charge de l'appelant : lui seul sait ce qu'il
 * doit redemander, et à quelle condition — un magasin vide n'a rien à recharger.
 */
export function surChangement(
  domaines: string[],
  rafraichir: () => void | Promise<void>,
): () => void {
  const a: Abonnement = { domaines, rafraichir };
  abonnements.add(a);
  return () => abonnements.delete(a);
}
